import shallowCompare from "common/utils/shallowCompare";

function noop() {}

// A stage may produce fields that it does not read from its input.
export type GenerationStage<Params, GenerateParams extends object = Partial<Params>> =
  | AsyncGenerationStage<Params, GenerateParams>
  | SyncGenerationStage<Params, GenerateParams>;

export interface GenerationStageBase<Params, GenerateParams extends object = Partial<Params>> {
  needsUpdate(optionSets: Partial<Params>): boolean;
  testIdempotence(optionSets: Partial<Params>): boolean;
  getLastValue(): GenerateParams;
  destroy(): void;
}

export interface SyncGenerationStage<Params, GenerateParams extends object = Partial<Params>>
  extends GenerationStageBase<Params, GenerateParams> {
  readonly type: "SYNC";
  generate(): GenerateParams;
}

export interface AsyncGenerationStage<Params, GenerateParams extends object = Partial<Params>>
  extends GenerationStageBase<Params, GenerateParams> {
  readonly type: "ASYNC";
  generate(): Promise<GenerateParams>;
}

/**
 * Every stage produced by `memo`/`memoAsync`. Stages are module-level singletons already held by
 * the stage sets in `../stages`, so this retains nothing extra.
 */
const allGenerationStages = new Set<{ destroy: () => void }>();

/**
 * Destroys the memoized value of every generation stage. Test-only, and deliberately not exported
 * from the package index.
 *
 * For test suites that generate many unrelated buildings in sequence, where the memoized values
 * are never hit again but keep a full building's worth of intermediate geometry resident.
 *
 * Must not run while a build is in flight. `generateBuildingWithStages` suspends at `await`s
 * between `needsUpdate` and `generate`, so clearing mid-build either drops `selectedParams` for a
 * stage about to run or turns a memo hit into a null `getLastValue()`, silently removing that
 * stage's output from the building. Calling this only between tests keeps that unreachable.
 */
export function destroyAllGenerationStagesForTests(): void {
  for (const stage of allGenerationStages) {
    stage.destroy();
  }
}

/**
 * memo returns an object with closures necessary to memoize a function with a select function.
 * One important contract of this data structure is that it is necessary to prime
 * the stage by first calling `needsUpdate`.
 */
export default function memo<Params, SelectParams extends object, GenerateParams extends object = Partial<Params>>(
  select: (p: Params) => SelectParams,
  func: (s: Readonly<SelectParams>) => GenerateParams,
  compare: (oldP: SelectParams, newP: SelectParams) => boolean = shallowCompare,
  destroy: (v: GenerateParams) => void = noop,
): SyncGenerationStage<Params, GenerateParams> {
  let memoizedReturnValue;
  let memoizedParams;
  let selectedParams;

  const stage: SyncGenerationStage<Params, GenerateParams> = {
    needsUpdate(optionSets: Params) {
      selectedParams = select(optionSets);

      return !compare(memoizedParams, selectedParams);
    },

    getLastValue() {
      return memoizedReturnValue;
    },

    // Test-specific method (not to be called by production code)
    destroy() {
      if (memoizedReturnValue) {
        destroy(memoizedReturnValue);
      }
      memoizedReturnValue = null;
      // The params must go too. `needsUpdate` compares against `memoizedParams`, so leaving them
      // set lets a later identical build take the memo path and assign the null value onto the
      // building. `selectedParams` holds references into the last build.
      memoizedParams = undefined;
      selectedParams = undefined;
    },

    testIdempotence(optionSets: Params) {
      const selectedParams1 = select(optionSets);
      const selectedParams2 = select(optionSets);
      return !compare(selectedParams1, selectedParams2);
    },

    type: "SYNC",

    generate() {
      const value = func(selectedParams);

      memoizedReturnValue = value;
      memoizedParams = selectedParams;

      return value;
    },
  };

  allGenerationStages.add(stage);

  return stage;
}

/**
 * memoAsync returns an object with closures necessary to memoize an async function with a select function.
 * One important contract of this data structure is that it is necessary to prime
 * the stage by first calling `needsUpdate`.
 */
export function memoAsync<Params, SelectParams extends object, GenerateParams extends object = Partial<Params>>(
  select: (p: Params) => SelectParams,
  func: (s: Readonly<SelectParams>) => Promise<GenerateParams>,
  compare: (oldP: SelectParams, newP: SelectParams) => boolean = shallowCompare,
  destroy: (v: GenerateParams) => void = noop,
): AsyncGenerationStage<Params, GenerateParams> {
  let memoizedReturnValue;
  let memoizedParams;
  let selectedParams;

  const stage: AsyncGenerationStage<Params, GenerateParams> = {
    needsUpdate(optionSets: Params) {
      selectedParams = select(optionSets);
      return !compare(memoizedParams, selectedParams);
    },

    getLastValue() {
      return memoizedReturnValue;
    },

    destroy() {
      if (memoizedReturnValue) {
        destroy(memoizedReturnValue);
      }
      memoizedReturnValue = null;
      // See the note on the sync stage's `destroy`.
      memoizedParams = undefined;
      selectedParams = undefined;
    },

    testIdempotence(optionSets: Params) {
      const selectedParams1 = select(optionSets);
      const selectedParams2 = select(optionSets);
      return !compare(selectedParams1, selectedParams2);
    },

    type: "ASYNC",

    async generate() {
      const value = await func(selectedParams);

      memoizedReturnValue = value;
      memoizedParams = selectedParams;

      return value;
    },
  };

  allGenerationStages.add(stage);

  return stage;
}
